import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Modal,
  Message,
  Tab,
} from 'semantic-ui-react';
import { API, removeTrailingSlash, showError, showSuccess } from '../helpers';
import globalStyles, { colors, radius } from '../styles/globalStyles';

const SystemSetting = () => {
  const { t } = useTranslation();
  let [inputs, setInputs] = useState({
    PasswordLoginEnabled: '',
    PasswordRegisterEnabled: '',
    EmailVerificationEnabled: '',
    GitHubOAuthEnabled: '',
    GitHubClientId: '',
    GitHubClientSecret: '',
    LarkClientId: '',
    LarkClientSecret: '',
    Notice: '',
    SMTPServer: '',
    SMTPPort: '',
    SMTPAccount: '',
    SMTPFrom: '',
    SMTPToken: '',
    ServerAddress: '',
    Footer: '',
    WeChatAuthEnabled: '',
    WeChatServerAddress: '',
    WeChatServerToken: '',
    WeChatAccountQRCodeImageURL: '',
    MessagePusherAddress: '',
    MessagePusherToken: '',
    TurnstileCheckEnabled: '',
    TurnstileSiteKey: '',
    TurnstileSecretKey: '',
    RegisterEnabled: '',
    EmailDomainRestrictionEnabled: '',
    EmailDomainWhitelist: '',
  });
  const [originInputs, setOriginInputs] = useState({});
  let [loading, setLoading] = useState(false);
  const [EmailDomainWhitelist, setEmailDomainWhitelist] = useState([]);
  const [restrictedDomainInput, setRestrictedDomainInput] = useState('');
  const [showPasswordWarningModal, setShowPasswordWarningModal] =
    useState(false);
  const [databaseSyncing, setDatabaseSyncing] = useState(false);
  const [showDatabaseSyncConfirm, setShowDatabaseSyncConfirm] = useState(false);

  const syncDatabase = async () => {
    setDatabaseSyncing(true);
    try {
      const res = await API.post('/api/database/sync');
      if (res.data.success) {
        showSuccess(t('setting.system.database_sync.success'));
        setShowDatabaseSyncConfirm(false);
      } else {
        // 修复：后端 200+success=false 且无 message 字段时原先静默无提示
        showError(res.data.message || res.data.error || t('setting.system.database_sync.failed'));
      }
    } catch (error) {
      // 修复：非 200 响应原先只取 error.message 丢掉 response body
      showError(error.response?.data?.message || error.response?.data?.error || error.message);
    } finally {
      setDatabaseSyncing(false);
    }
  };

  const [proxyConfig, setProxyConfig] = useState({
    user_id: '1074352',
    key: '9ef4549f2bc14e218639aaebe3d8f29a',
    account_key: '25cb6df056814d90b1e7e69e9566934c',
    trade_no: '6452776841368334',
    pt: 1,
    province: '广东',
  });
  const [proxyBalance, setProxyBalance] = useState(null);
  const [proxyTesting, setProxyTesting] = useState(false);

  // 通讯加密设置
  const [encryption, setEncryption] = useState({
    key: '',
    salt: '',
  });
  const [encryptionRules, setEncryptionRules] = useState([]);
  const [encryptionTestText, setEncryptionTestText] = useState('账号catfee904649854,密码sk-32467687324dssds32532');
  const [encryptionTestOutput, setEncryptionTestOutput] = useState('');
  const [encryptionUsage, setEncryptionUsage] = useState(0);
  const [encryptionLoading, setEncryptionLoading] = useState(false);
  const [showEncryptionConfirm, setShowEncryptionConfirm] = useState(false);

  // ngrok 内网穿透
  const [ngrokConfig, setNgrokConfig] = useState({
    enabled: false,
    authtoken: '',
    domain: '',
    region: '',
  });
  const [ngrokStatus, setNgrokStatus] = useState({
    running: false,
    public_url: '',
    error: '',
    since: 0,
  });
  const [ngrokLoading, setNgrokLoading] = useState(false);

  // 自建 ws 反向隧道
  const [tunnelConfig, setTunnelConfig] = useState({
    enabled: false,
    server_url: '',
    token: '',
    public_url: '',
  });
  const [tunnelStatus, setTunnelStatus] = useState({
    running: false,
    public_url: '',
    error: '',
    since: 0,
    latency_ms: 0,
    last_ping_at: 0,
    last_request_ms: 0,
  });
  const [tunnelLoading, setTunnelLoading] = useState(false);
  const [tunnelTesting, setTunnelTesting] = useState(false);

  // FRP 内网穿透
  const [frpConfig, setFrpConfig] = useState({
    enabled: false,
    server_addr: '',
    server_port: 16666,
    auth_token: '',
    local_port: 3000,
    remote_port: 3000,
    custom_domain: '',
    proxy_name: 'catfee-api',
    proxy_type: 'tcp',
  });
  const [frpStatus, setFrpStatus] = useState({
    running: false,
    state: 'stopped',
    public_url: '',
    error: '',
    since: 0,
    pid: 0,
  });
  const [frpLoading, setFrpLoading] = useState(false);

  const getOptions = async () => {
    const res = await API.get('/api/option/');
    const { success, message, data } = res.data;
    if (success) {
      let newInputs = {};
      data.forEach((item) => {
        newInputs[item.key] = item.value;
      });
      setInputs({
        ...newInputs,
        EmailDomainWhitelist: newInputs.EmailDomainWhitelist.split(','),
      });
      setOriginInputs(newInputs);

      if (newInputs.JuliangProxyConfig) {
        try {
          const parsed = JSON.parse(newInputs.JuliangProxyConfig);
          setProxyConfig({
            user_id: parsed.user_id || '',
            key: parsed.key || '',
            account_key: parsed.account_key || '',
            trade_no: parsed.trade_no || '',
            pt: parsed.pt || 1,
            province: parsed.province || '',
          });
        } catch (e) { /* ignore parse error */ }
      }

      setEmailDomainWhitelist(
        newInputs.EmailDomainWhitelist.split(',').map((item) => {
          return { key: item, text: item, value: item };
        })
      );
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    getOptions().then();
    getEncryptionConfig().then();
    getEncryptionUsage().then();
    getNgrokConfig().then();
    getNgrokStatus().then();
    getTunnelConfig().then();
    getTunnelStatus().then();
    getFrpConfig().then();
    getFrpStatus().then();

    const tunnelTimer = setInterval(getTunnelStatus, 5000);
    const frpTimer = setInterval(getFrpStatus, 5000);
    return () => { clearInterval(tunnelTimer); clearInterval(frpTimer); };
  }, []);

  const getEncryptionConfig = async () => {
    const res = await API.get('/api/encryption/');
    const { success, message, data } = res.data;
    if (success) {
      setEncryption({
        key: data.key || '',
        salt: data.salt || '',
      });
      setEncryptionRules(data.rules || []);
    } else {
      showError(message);
    }
  };

  const getEncryptionUsage = async () => {
    const res = await API.get('/api/encryption/usage');
    const { success, message, data } = res.data;
    if (success) {
      setEncryptionUsage(data || 0);
    } else {
      showError(message);
    }
  };

  const doGenerateKey = async () => {
    setEncryptionLoading(true);
    try {
      const res = await API.post('/api/encryption/generate');
      const { success, message, data } = res.data;
      if (success) {
        setEncryption({
          key: data.key || '',
          salt: encryption.salt,
        });
        showSuccess('密钥已重新生成');
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setEncryptionLoading(false);
    setShowEncryptionConfirm(false);
  };

  const handleGenerateKey = () => {
    if (encryptionUsage > 0) {
      setShowEncryptionConfirm(true);
    } else {
      doGenerateKey();
    }
  };

  const saveEncryptionSalt = async () => {
    setEncryptionLoading(true);
    const res = await API.put('/api/option/', {
      key: 'EncryptionSalt',
      value: encryption.salt,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('加密盐已保存');
    } else {
      showError(message);
    }
    setEncryptionLoading(false);
  };

  const updateEncryptionRule = (index, patch) => {
    setEncryptionRules(prev => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const addEncryptionRule = () => {
    setEncryptionRules(prev => [...prev, { name: '自定义规则', pattern: '', enabled: true }]);
  };

  const removeEncryptionRule = (index) => {
    setEncryptionRules(prev => prev.filter((_, i) => i !== index));
  };

  const saveEncryptionRules = async () => {
    setEncryptionLoading(true);
    try {
      const res = await API.put('/api/encryption/rules', { rules: encryptionRules });
      const { success, message, data } = res.data;
      if (success) {
        setEncryptionRules(data.rules || encryptionRules);
        showSuccess('正则规则已保存');
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setEncryptionLoading(false);
  };

  const resetEncryptionRules = async () => {
    setEncryptionLoading(true);
    try {
      const res = await API.post('/api/encryption/rules/reset');
      const { success, message, data } = res.data;
      if (success) {
        setEncryptionRules(data.rules || []);
        setEncryptionTestOutput('');
        showSuccess('已恢复默认规则');
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setEncryptionLoading(false);
  };

  const testEncryptionRules = async () => {
    setEncryptionLoading(true);
    try {
      const res = await API.post('/api/encryption/test', {
        text: encryptionTestText,
        rules: encryptionRules,
      });
      const { success, message, data } = res.data;
      if (success) {
        setEncryptionTestOutput(data.output || '');
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setEncryptionLoading(false);
  };

  const updateOption = async (key, value) => {
    setLoading(true);
    switch (key) {
      case 'PasswordLoginEnabled':
      case 'PasswordRegisterEnabled':
      case 'EmailVerificationEnabled':
      case 'GitHubOAuthEnabled':
      case 'WeChatAuthEnabled':
      case 'TurnstileCheckEnabled':
      case 'EmailDomainRestrictionEnabled':
      case 'RegisterEnabled':
        value = inputs[key] === 'true' ? 'false' : 'true';
        break;
      default:
        break;
    }
    const res = await API.put('/api/option/', {
      key,
      value,
    });
    const { success, message } = res.data;
    if (success) {
      if (key === 'EmailDomainWhitelist') {
        value = value.split(',');
      }
      setInputs((inputs) => ({
        ...inputs,
        [key]: value,
      }));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const handleInputChange = async (e, { name, value }) => {
    if (name === 'PasswordLoginEnabled' && inputs[name] === 'true') {
      // block disabling password login
      setShowPasswordWarningModal(true);
      return;
    }
    if (
      name === 'Notice' ||
      name.startsWith('SMTP') ||
      name === 'ServerAddress' ||
      name === 'GitHubClientId' ||
      name === 'GitHubClientSecret' ||
      name === 'LarkClientId' ||
      name === 'LarkClientSecret' ||
      name === 'WeChatServerAddress' ||
      name === 'WeChatServerToken' ||
      name === 'WeChatAccountQRCodeImageURL' ||
      name === 'TurnstileSiteKey' ||
      name === 'TurnstileSecretKey' ||
      name === 'EmailDomainWhitelist'
    ) {
      setInputs((inputs) => ({ ...inputs, [name]: value }));
    } else {
      await updateOption(name, value);
    }
  };

  const submitServerAddress = async () => {
    let ServerAddress = removeTrailingSlash(inputs.ServerAddress);
    await updateOption('ServerAddress', ServerAddress);
  };

  const submitSMTP = async () => {
    if (originInputs['SMTPServer'] !== inputs.SMTPServer) {
      await updateOption('SMTPServer', inputs.SMTPServer);
    }
    if (originInputs['SMTPAccount'] !== inputs.SMTPAccount) {
      await updateOption('SMTPAccount', inputs.SMTPAccount);
    }
    if (originInputs['SMTPFrom'] !== inputs.SMTPFrom) {
      await updateOption('SMTPFrom', inputs.SMTPFrom);
    }
    if (
      originInputs['SMTPPort'] !== inputs.SMTPPort &&
      inputs.SMTPPort !== ''
    ) {
      await updateOption('SMTPPort', inputs.SMTPPort);
    }
    if (
      originInputs['SMTPToken'] !== inputs.SMTPToken &&
      inputs.SMTPToken !== ''
    ) {
      await updateOption('SMTPToken', inputs.SMTPToken);
    }
  };

  const submitEmailDomainWhitelist = async () => {
    if (
      originInputs['EmailDomainWhitelist'] !==
        inputs.EmailDomainWhitelist.join(',') &&
      inputs.SMTPToken !== ''
    ) {
      await updateOption(
        'EmailDomainWhitelist',
        inputs.EmailDomainWhitelist.join(',')
      );
    }
  };

  const submitWeChat = async () => {
    if (originInputs['WeChatServerAddress'] !== inputs.WeChatServerAddress) {
      await updateOption(
        'WeChatServerAddress',
        removeTrailingSlash(inputs.WeChatServerAddress)
      );
    }
    if (
      originInputs['WeChatAccountQRCodeImageURL'] !==
      inputs.WeChatAccountQRCodeImageURL
    ) {
      await updateOption(
        'WeChatAccountQRCodeImageURL',
        inputs.WeChatAccountQRCodeImageURL
      );
    }
    if (
      originInputs['WeChatServerToken'] !== inputs.WeChatServerToken &&
      inputs.WeChatServerToken !== ''
    ) {
      await updateOption('WeChatServerToken', inputs.WeChatServerToken);
    }
  };

  const submitMessagePusher = async () => {
    if (originInputs['MessagePusherAddress'] !== inputs.MessagePusherAddress) {
      await updateOption(
        'MessagePusherAddress',
        removeTrailingSlash(inputs.MessagePusherAddress)
      );
    }
    if (
      originInputs['MessagePusherToken'] !== inputs.MessagePusherToken &&
      inputs.MessagePusherToken !== ''
    ) {
      await updateOption('MessagePusherToken', inputs.MessagePusherToken);
    }
  };

  const submitGitHubOAuth = async () => {
    if (originInputs['GitHubClientId'] !== inputs.GitHubClientId) {
      await updateOption('GitHubClientId', inputs.GitHubClientId);
    }
    if (
      originInputs['GitHubClientSecret'] !== inputs.GitHubClientSecret &&
      inputs.GitHubClientSecret !== ''
    ) {
      await updateOption('GitHubClientSecret', inputs.GitHubClientSecret);
    }
  };

  const submitLarkOAuth = async () => {
    if (originInputs['LarkClientId'] !== inputs.LarkClientId) {
      await updateOption('LarkClientId', inputs.LarkClientId);
    }
    if (
      originInputs['LarkClientSecret'] !== inputs.LarkClientSecret &&
      inputs.LarkClientSecret !== ''
    ) {
      await updateOption('LarkClientSecret', inputs.LarkClientSecret);
    }
  };

  const submitTurnstile = async () => {
    if (originInputs['TurnstileSiteKey'] !== inputs.TurnstileSiteKey) {
      await updateOption('TurnstileSiteKey', inputs.TurnstileSiteKey);
    }
    if (
      originInputs['TurnstileSecretKey'] !== inputs.TurnstileSecretKey &&
      inputs.TurnstileSecretKey !== ''
    ) {
      await updateOption('TurnstileSecretKey', inputs.TurnstileSecretKey);
    }
  };

  const submitProxyConfig = async () => {
    setLoading(true);
    try {
      const jsonValue = JSON.stringify(proxyConfig);
      await updateOption('JuliangProxyConfig', jsonValue);
      showSuccess(t('setting.system.proxy.messages.save_success'));
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  const testProxyConnection = async () => {
    setProxyTesting(true);
    try {
      const res = await API.get('/api/proxy/test');
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('setting.system.proxy.messages.test_success', { ip: data.ip, port: data.port }));
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setProxyTesting(false);
  };

  const queryProxyBalance = async () => {
    try {
      const res = await API.get('/api/proxy/balance');
      const { success, message, data } = res.data;
      if (success) {
        setProxyBalance(data.balance);
        showSuccess(t('setting.system.proxy.messages.balance_result', { amount: data.balance.toFixed(2) }));
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const getNgrokConfig = async () => {
    try {
      const res = await API.get('/api/ngrok/config');
      const { success, message, data } = res.data;
      if (success) {
        setNgrokConfig({
          enabled: !!data.enabled,
          authtoken: data.authtoken || '',
          domain: data.domain || '',
          region: data.region || '',
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const getNgrokStatus = async () => {
    try {
      const res = await API.get('/api/ngrok/status');
      const { success, message, data } = res.data;
      if (success) {
        setNgrokStatus({
          running: !!data.running,
          public_url: data.public_url || '',
          error: data.error || '',
          since: data.since || 0,
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const saveNgrokConfig = async () => {
    setNgrokLoading(true);
    try {
      const res = await API.put('/api/ngrok/config', {
        enabled: ngrokConfig.enabled,
        authtoken: ngrokConfig.authtoken,
        domain: ngrokConfig.domain,
        region: ngrokConfig.region,
      });
      const { success, message, data } = res.data;
      if (success) {
        setNgrokConfig((prev) => ({
          ...prev,
          authtoken: data.authtoken || prev.authtoken,
        }));
        showSuccess(t('setting.system.ngrok.messages.save_success'));
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setNgrokLoading(false);
  };

  const startNgrok = async () => {
    setNgrokLoading(true);
    try {
      const res = await API.post('/api/ngrok/start');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.system.ngrok.messages.start_success'));
        setTimeout(getNgrokStatus, 1500);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setNgrokLoading(false);
  };

  const stopNgrok = async () => {
    setNgrokLoading(true);
    try {
      const res = await API.post('/api/ngrok/stop');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.system.ngrok.messages.stop_success'));
        setTimeout(getNgrokStatus, 500);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setNgrokLoading(false);
  };

  const copyNgrokUrl = async () => {
    if (!ngrokStatus.public_url) return;
    try {
      await navigator.clipboard.writeText(ngrokStatus.public_url);
      showSuccess(t('setting.system.ngrok.messages.copy_success'));
    } catch (e) {
      showError(e.message);
    }
  };

  const getTunnelConfig = async () => {
    try {
      const res = await API.get('/api/tunnel/config');
      const { success, message, data } = res.data;
      if (success) {
        setTunnelConfig({
          enabled: !!data.enabled,
          server_url: data.server_url || '',
          token: data.token || '',
          public_url: data.public_url || '',
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const getTunnelStatus = async () => {
    try {
      const res = await API.get('/api/tunnel/status');
      const { success, message, data } = res.data;
      if (success) {
        setTunnelStatus({
          running: !!data.running,
          public_url: data.public_url || '',
          error: data.error || '',
          since: data.since || 0,
          latency_ms: data.latency_ms || 0,
          last_ping_at: data.last_ping_at || 0,
          last_request_ms: data.last_request_ms || 0,
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const saveTunnelConfig = async () => {
    setTunnelLoading(true);
    try {
      const res = await API.put('/api/tunnel/config', {
        enabled: tunnelConfig.enabled,
        server_url: tunnelConfig.server_url,
        token: tunnelConfig.token,
        public_url: tunnelConfig.public_url,
      });
      const { success, message, data } = res.data;
      if (success) {
        setTunnelConfig((prev) => ({
          ...prev,
          token: data.token || prev.token,
        }));
        showSuccess(t('setting.system.tunnel.messages.save_success'));
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setTunnelLoading(false);
  };

  const startTunnel = async () => {
    setTunnelLoading(true);
    try {
      const res = await API.post('/api/tunnel/start');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.system.tunnel.messages.start_success'));
        setTimeout(getTunnelStatus, 1500);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setTunnelLoading(false);
  };

  const stopTunnel = async () => {
    setTunnelLoading(true);
    try {
      const res = await API.post('/api/tunnel/stop');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.system.tunnel.messages.stop_success'));
        setTimeout(getTunnelStatus, 500);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setTunnelLoading(false);
  };

  const testTunnel = async () => {
    setTunnelTesting(true);
    try {
      // 传入当前表单值，便于保存前测试；为空时后端用已保存配置
      const res = await API.post('/api/tunnel/test', {
        server_url: tunnelConfig.server_url,
        token: tunnelConfig.token,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('setting.system.tunnel.messages.test_success', { latency: data.latency_ms }));
        getTunnelStatus();
      } else {
        showError(t('setting.system.tunnel.messages.test_fail') + ': ' + message);
      }
    } catch (e) {
      showError(e.message);
    }
    setTunnelTesting(false);
  };

  const copyTunnelUrl = async () => {
    if (!tunnelStatus.public_url) return;
    try {
      await navigator.clipboard.writeText(tunnelStatus.public_url);
      showSuccess(t('setting.system.tunnel.messages.copy_success'));
    } catch (e) {
      showError(e.message);
    }
  };

  // ===== FRP =====
  const getFrpConfig = async () => {
    try {
      const res = await API.get('/api/frp/config');
      const { success, message, data } = res.data;
      if (success) {
        setFrpConfig({
          enabled: !!data.enabled,
          server_addr: data.server_addr || '',
          server_port: data.server_port || 16666,
          auth_token: data.auth_token || '',
          local_port: data.local_port || 3000,
          remote_port: data.remote_port || 3000,
          custom_domain: data.custom_domain || '',
          proxy_name: data.proxy_name || 'catfee-api',
          proxy_type: data.proxy_type || 'tcp',
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const getFrpStatus = async () => {
    try {
      const res = await API.get('/api/frp/status');
      const { success, message, data } = res.data;
      if (success) {
        setFrpStatus({
          running: !!data.running,
          state: data.state || (data.running ? 'running' : 'stopped'),
          public_url: data.public_url || '',
          error: data.error || '',
          since: data.since || 0,
          pid: data.pid || 0,
        });
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const saveFrpConfig = async () => {
    setFrpLoading(true);
    try {
      const res = await API.put('/api/frp/config', {
        enabled: frpConfig.enabled,
        server_addr: frpConfig.server_addr,
        server_port: frpConfig.server_port,
        auth_token: frpConfig.auth_token,
        local_port: frpConfig.local_port,
        remote_port: frpConfig.remote_port,
        custom_domain: frpConfig.custom_domain,
        proxy_name: frpConfig.proxy_name,
        proxy_type: frpConfig.proxy_type,
      });
      const { success, message, data, reloaded } = res.data;
      if (success) {
        setFrpConfig((prev) => ({
          ...prev,
          auth_token: data.auth_token || prev.auth_token,
        }));
        showSuccess(t(reloaded
          ? 'setting.system.frp.messages.save_reloaded'
          : 'setting.system.frp.messages.save_success'));
        getFrpStatus();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setFrpLoading(false);
  };

  const startFrp = async () => {
    setFrpLoading(true);
    try {
      const res = await API.post('/api/frp/start');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.system.frp.messages.start_success'));
        setTimeout(getFrpStatus, 1500);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setFrpLoading(false);
  };

  const stopFrp = async () => {
    setFrpLoading(true);
    try {
      const res = await API.post('/api/frp/stop');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('setting.system.frp.messages.stop_success'));
        setTimeout(getFrpStatus, 500);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setFrpLoading(false);
  };

  const copyFrpUrl = async () => {
    if (!frpStatus.public_url) return;
    try {
      await navigator.clipboard.writeText(frpStatus.public_url);
      showSuccess(t('setting.system.frp.messages.copy_success'));
    } catch (e) {
      showError(e.message);
    }
  };

  const submitNewRestrictedDomain = () => {
    const localDomainList = inputs.EmailDomainWhitelist;
    if (
      restrictedDomainInput !== '' &&
      !localDomainList.includes(restrictedDomainInput)
    ) {
      setRestrictedDomainInput('');
      setInputs({
        ...inputs,
        EmailDomainWhitelist: [...localDomainList, restrictedDomainInput],
      });
      setEmailDomainWhitelist([
        ...EmailDomainWhitelist,
        {
          key: restrictedDomainInput,
          text: restrictedDomainInput,
          value: restrictedDomainInput,
        },
      ]);
    }
  };

  const frpState = ['stopped', 'starting', 'running', 'failed'].includes(frpStatus.state)
    ? frpStatus.state
    : (frpStatus.running ? 'running' : 'stopped');
  const frpStateStyle = {
    stopped: { background: 'rgba(0,0,0,0.06)', color: colors.gray },
    starting: { background: 'rgba(245,158,11,0.1)', color: '#d97706' },
    running: { background: 'rgba(34,197,94,0.1)', color: '#16a34a' },
    failed: { background: 'rgba(220,38,38,0.1)', color: '#dc2626' },
  }[frpState];

  return (
    <div className='settings-setting-view settings-system'>
    <Grid columns={1}>
      <Grid.Column>
        <Form loading={loading} className='settings-form-stack'>
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>{t('setting.system.database_sync.title')}</Header>
          <p>{t('setting.system.database_sync.description')}</p>
          <Button
            style={globalStyles.primaryButton}
            loading={databaseSyncing}
            disabled={databaseSyncing}
            onClick={() => setShowDatabaseSyncConfirm(true)}
          >
            {t('setting.system.database_sync.button')}
          </Button>
          <Modal
            open={showDatabaseSyncConfirm}
            onClose={() => !databaseSyncing && setShowDatabaseSyncConfirm(false)}
            size='tiny'
            className='studio-modal'
          >
            <Modal.Header>{t('setting.system.database_sync.confirm_title')}</Modal.Header>
            <Modal.Content><p>{t('setting.system.database_sync.confirm_content')}</p></Modal.Content>
            <Modal.Actions>
              <Button onClick={() => setShowDatabaseSyncConfirm(false)} disabled={databaseSyncing}>
                {t('setting.system.database_sync.cancel')}
              </Button>
              <Button
                color='orange'
                loading={databaseSyncing}
                disabled={databaseSyncing}
                onClick={syncDatabase}
              >
                {t('setting.system.database_sync.confirm')}
              </Button>
            </Modal.Actions>
          </Modal>
          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>{t('setting.system.general.title')}</Header>
          <Form.Group widths='equal'>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.general.server_address')}
              placeholder={t(
                'setting.system.general.server_address_placeholder'
              )}
              value={inputs.ServerAddress}
              name='ServerAddress'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button style={globalStyles.primaryButton} onClick={submitServerAddress}>
            {t('setting.system.general.buttons.update')}
          </Form.Button>
          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>{t('setting.system.login.title')}</Header>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.PasswordLoginEnabled === 'true'}
              label={t('setting.system.login.password_login')}
              name='PasswordLoginEnabled'
              onChange={handleInputChange}
            />
            {showPasswordWarningModal && (
              <Modal
                open={showPasswordWarningModal}
                onClose={() => setShowPasswordWarningModal(false)}
                size={'tiny'}
                className='studio-modal'
                style={{ maxWidth: '450px' }}
              >
                <Modal.Header>
                  {t('setting.system.password_login.warning.title')}
                </Modal.Header>
                <Modal.Content>
                  <p>{t('setting.system.password_login.warning.content')}</p>
                </Modal.Content>
                <Modal.Actions>
                  <Button onClick={() => setShowPasswordWarningModal(false)}>
                    {t('setting.system.password_login.warning.buttons.cancel')}
                  </Button>
                  <Button
                    color='yellow'
                    onClick={async () => {
                      setShowPasswordWarningModal(false);
                      await updateOption('PasswordLoginEnabled', 'false');
                    }}
                  >
                    {t('setting.system.password_login.warning.buttons.confirm')}
                  </Button>
                </Modal.Actions>
              </Modal>
            )}
            <Form.Checkbox
              checked={inputs.PasswordRegisterEnabled === 'true'}
              label={t('setting.system.login.password_register')}
              name='PasswordRegisterEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.EmailVerificationEnabled === 'true'}
              label={t('setting.system.login.email_verification')}
              name='EmailVerificationEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.GitHubOAuthEnabled === 'true'}
              label={t('setting.system.login.github_oauth')}
              name='GitHubOAuthEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.WeChatAuthEnabled === 'true'}
              label={t('setting.system.login.wechat_login')}
              name='WeChatAuthEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.RegisterEnabled === 'true'}
              label={t('setting.system.login.registration')}
              name='RegisterEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.TurnstileCheckEnabled === 'true'}
              label={t('setting.system.login.turnstile')}
              name='TurnstileCheckEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>{t('setting.system.email_restriction.title')}</Header>
          <Message style={{ borderRadius: 10, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)' }}>{t('setting.system.email_restriction.subtitle')}</Message>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.EmailDomainRestrictionEnabled === 'true'}
              label={t('setting.system.email_restriction.enable')}
              name='EmailDomainRestrictionEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.email_restriction.add_domain')}
              placeholder={t(
                'setting.system.email_restriction.add_domain_placeholder'
              )}
              value={restrictedDomainInput}
              onChange={(e, { value }) => {
                setRestrictedDomainInput(value);
              }}
              action={
                <Button
                  onClick={() => {
                    if (restrictedDomainInput === '') return;
                    setEmailDomainWhitelist([
                      ...EmailDomainWhitelist,
                      {
                        key: restrictedDomainInput,
                        text: restrictedDomainInput,
                        value: restrictedDomainInput,
                      },
                    ]);
                    setRestrictedDomainInput('');
                  }}
                >
                  {t('setting.system.email_restriction.buttons.fill')}
                </Button>
              }
            />
          </Form.Group>
          <Form.Dropdown style={{ borderRadius: 10 }}
            label={t('setting.system.email_restriction.allowed_domains')}
            placeholder={t('setting.system.email_restriction.allowed_domains')}
            fluid
            multiple
            search
            selection
            allowAdditions
            value={EmailDomainWhitelist.map((item) => item.value)}
            options={EmailDomainWhitelist}
            onAddItem={(e, { value }) => {
              setEmailDomainWhitelist([
                ...EmailDomainWhitelist,
                {
                  key: value,
                  text: value,
                  value: value,
                },
              ]);
            }}
            onChange={(e, { value }) => {
              let newEmailDomainWhitelist = [];
              value.forEach((item) => {
                newEmailDomainWhitelist.push({
                  key: item,
                  text: item,
                  value: item,
                });
              });
              setEmailDomainWhitelist(newEmailDomainWhitelist);
            }}
          />
          <Form.Button style={globalStyles.primaryButton} onClick={submitEmailDomainWhitelist}>
            {t('setting.system.email_restriction.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>{t('setting.system.smtp.title')}</Header>
          <Message style={{ borderRadius: 10, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)' }}>{t('setting.system.smtp.subtitle')}</Message>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.smtp.server')}
              placeholder={t('setting.system.smtp.server_placeholder')}
              name='SMTPServer'
              onChange={handleInputChange}
              value={inputs.SMTPServer}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.smtp.port')}
              placeholder={t('setting.system.smtp.port_placeholder')}
              name='SMTPPort'
              onChange={handleInputChange}
              value={inputs.SMTPPort}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.smtp.account')}
              placeholder={t('setting.system.smtp.account_placeholder')}
              name='SMTPAccount'
              onChange={handleInputChange}
              value={inputs.SMTPAccount}
            />
          </Form.Group>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.smtp.from')}
              placeholder={t('setting.system.smtp.from_placeholder')}
              name='SMTPFrom'
              onChange={handleInputChange}
              value={inputs.SMTPFrom}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.smtp.token')}
              placeholder={t('setting.system.smtp.token_placeholder')}
              name='SMTPToken'
              onChange={handleInputChange}
              type='password'
              value={inputs.SMTPToken}
            />
          </Form.Group>
          <Form.Button style={globalStyles.primaryButton} onClick={submitSMTP}>
            {t('setting.system.smtp.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>
            {t('setting.system.github.title')}
            <Header.Subheader>
              {t('setting.system.github.subtitle')}
              <a href='https://github.com/settings/developers' target='_blank'>
                {t('setting.system.github.manage_link')}
              </a>
              {t('setting.system.github.manage_text')}
            </Header.Subheader>
          </Header>
          <Message style={{ borderRadius: 10, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)' }}>
            {t('setting.system.github.subtitle')}
            <a href='https://github.com/settings/developers' target='_blank'>
              {t('setting.system.github.manage_link')}
            </a>
            {t('setting.system.github.manage_text')}
          </Message>
          <Message style={{ borderRadius: 10, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)' }}>
            {t('setting.system.github.url_notice', {
              server_url: originInputs.ServerAddress,
              callback_url: `${originInputs.ServerAddress}/oauth/github`,
            })}
          </Message>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.github.client_id')}
              placeholder={t('setting.system.github.client_id_placeholder')}
              name='GitHubClientId'
              onChange={handleInputChange}
              value={inputs.GitHubClientId}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.github.client_secret')}
              placeholder={t('setting.system.github.client_secret_placeholder')}
              name='GitHubClientSecret'
              onChange={handleInputChange}
              type='password'
              value={inputs.GitHubClientSecret}
            />
          </Form.Group>
          <Form.Button style={globalStyles.primaryButton} onClick={submitGitHubOAuth}>
            {t('setting.system.github.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>
            {t('setting.system.lark.title')}
            <Header.Subheader>
              {t('setting.system.lark.subtitle')}
              <a href='https://open.feishu.cn/app' target='_blank'>
                {t('setting.system.lark.manage_link')}
              </a>
              {t('setting.system.lark.manage_text')}
            </Header.Subheader>
          </Header>
          <Message style={{ borderRadius: 10, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)' }}>
            {t('setting.system.lark.url_notice', {
              server_url: inputs.ServerAddress,
              callback_url: `${inputs.ServerAddress}/oauth/lark`,
            })}
          </Message>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.lark.client_id')}
              name='LarkClientId'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.LarkClientId}
              placeholder={t('setting.system.lark.client_id_placeholder')}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.lark.client_secret')}
              name='LarkClientSecret'
              onChange={handleInputChange}
              type='password'
              autoComplete='new-password'
              value={inputs.LarkClientSecret}
              placeholder={t('setting.system.lark.client_secret_placeholder')}
            />
          </Form.Group>
          <Form.Button style={globalStyles.primaryButton} onClick={submitLarkOAuth}>
            {t('setting.system.lark.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>
            {t('setting.system.wechat.title')}
            <Header.Subheader>
              {t('setting.system.wechat.subtitle')}
              <a
                href='https://github.com/songquanpeng/wechat-server'
                target='_blank'
              >
                {t('setting.system.wechat.learn_more')}
              </a>
            </Header.Subheader>
          </Header>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.wechat.server_address')}
              name='WeChatServerAddress'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.WeChatServerAddress}
              placeholder={t(
                'setting.system.wechat.server_address_placeholder'
              )}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.wechat.token')}
              name='WeChatServerToken'
              onChange={handleInputChange}
              type='password'
              autoComplete='new-password'
              value={inputs.WeChatServerToken}
              placeholder={t('setting.system.wechat.token_placeholder')}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.wechat.qrcode')}
              name='WeChatAccountQRCodeImageURL'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.WeChatAccountQRCodeImageURL}
              placeholder={t('setting.system.wechat.qrcode_placeholder')}
            />
          </Form.Group>
          <Form.Button style={globalStyles.primaryButton} onClick={submitWeChat}>
            {t('setting.system.wechat.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: '#18181b', marginBottom: 16 }}>
            {t('setting.system.turnstile.title')}
            <Header.Subheader>
              {t('setting.system.turnstile.subtitle')}
              <a href='https://dash.cloudflare.com/' target='_blank'>
                {t('setting.system.turnstile.manage_link')}
              </a>
              {t('setting.system.turnstile.manage_text')}
            </Header.Subheader>
          </Header>
          <Form.Group widths={3}>
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.turnstile.site_key')}
              name='TurnstileSiteKey'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.TurnstileSiteKey}
              placeholder={t('setting.system.turnstile.site_key_placeholder')}
            />
            <Form.Input style={{ borderRadius: 10 }}
              label={t('setting.system.turnstile.secret_key')}
              name='TurnstileSecretKey'
              onChange={handleInputChange}
              type='password'
              autoComplete='new-password'
              value={inputs.TurnstileSecretKey}
              placeholder={t('setting.system.turnstile.secret_key_placeholder')}
            />
          </Form.Group>
          <Form.Button style={globalStyles.primaryButton} onClick={submitTurnstile}>
            {t('setting.system.turnstile.buttons.save')}
          </Form.Button>
        </Form>
        <Divider />
        <Form loading={loading} className='settings-form-stack'>
        <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: colors.gray, marginTop: 24, marginBottom: 24 }}>
          {t('setting.system.proxy.title')}
          <Header.Subheader style={{ marginTop: 6 }}>
            {t('setting.system.proxy.subtitle')}
          </Header.Subheader>
        </Header>
        {proxyBalance !== null && (
          <Message positive style={{ borderRadius: radius.md, marginBottom: 16 }}>
            <Message.Header>{t('setting.system.proxy.balance')}</Message.Header>
            <p>¥{proxyBalance.toFixed(2)}</p>
          </Message>
        )}
        <Form.Group widths='equal'>
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.proxy.user_id')}
            placeholder={t('setting.system.proxy.user_id_placeholder')}
            value={proxyConfig.user_id}
            onChange={(e, { value }) => setProxyConfig(prev => ({ ...prev, user_id: value }))}
          />
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.proxy.api_key')}
            placeholder={t('setting.system.proxy.api_key_placeholder')}
            type='password'
            autoComplete='new-password'
            value={proxyConfig.key}
            onChange={(e, { value }) => setProxyConfig(prev => ({ ...prev, key: value }))}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.proxy.account_key')}
            placeholder={t('setting.system.proxy.account_key_placeholder')}
            type='password'
            autoComplete='new-password'
            value={proxyConfig.account_key}
            onChange={(e, { value }) => setProxyConfig(prev => ({ ...prev, account_key: value }))}
          />
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.proxy.trade_no')}
            placeholder={t('setting.system.proxy.trade_no_placeholder')}
            value={proxyConfig.trade_no}
            onChange={(e, { value }) => setProxyConfig(prev => ({ ...prev, trade_no: value }))}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Select
            label={t('setting.system.proxy.protocol_type')}
            style={{ borderRadius: radius.md }}
            value={proxyConfig.pt}
            options={[
              { key: 1, text: 'HTTP', value: 1 },
              { key: 5, text: 'HTTPS', value: 5 },
              { key: 2, text: 'SOCKS5', value: 2 },
            ]}
            onChange={(e, { value }) => setProxyConfig(prev => ({ ...prev, pt: value }))}
          />
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.proxy.province_filter')}
            placeholder={t('setting.system.proxy.province_filter_placeholder')}
            value={proxyConfig.province}
            onChange={(e, { value }) => setProxyConfig(prev => ({ ...prev, province: value }))}
          />
        </Form.Group>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button style={globalStyles.primaryButton} onClick={submitProxyConfig}>
            {t('setting.system.proxy.buttons.save')}
          </Button>
          <Button style={globalStyles.button} onClick={testProxyConnection} loading={proxyTesting}>
            {t('setting.system.proxy.buttons.test')}
          </Button>
          <Button style={globalStyles.button} onClick={queryProxyBalance}>
            {t('setting.system.proxy.buttons.balance')}
          </Button>
        </div>
        </Form>

        <Divider />
        <Form loading={ngrokLoading} className='settings-form-stack'>
        <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: colors.gray, marginTop: 24, marginBottom: 16 }}>
          {t('setting.system.ngrok.title')}
          <Header.Subheader style={{ marginTop: 6 }}>
            {t('setting.system.ngrok.subtitle')}
          </Header.Subheader>
        </Header>
        <Message style={{ borderRadius: radius.md, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)', marginBottom: 16 }}>
          <Message.Header>{t('setting.system.ngrok.public_url')}</Message.Header>
          {ngrokStatus.running ? (
            <p style={{ marginBottom: 6 }}>
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#f0fdf4', color: '#16a34a', fontSize: 12, marginRight: 8 }}>
                {t('setting.system.ngrok.status_running')}
              </span>
              {ngrokStatus.public_url ? (
                <a href={ngrokStatus.public_url} target='_blank' rel='noopener noreferrer'>{ngrokStatus.public_url}</a>
              ) : t('setting.system.ngrok.no_public_url')}
              {ngrokStatus.public_url && (
                <Button size='mini' style={{ ...globalStyles.button, marginLeft: 8 }} onClick={copyNgrokUrl}>
                  {t('setting.system.ngrok.buttons.copy')}
                </Button>
              )}
            </p>
          ) : (
            <p style={{ marginBottom: 6 }}>
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#f5f5f5', color: '#6b7280', fontSize: 12, marginRight: 8 }}>
                {t('setting.system.ngrok.status_stopped')}
              </span>
            </p>
          )}
          {ngrokStatus.error && (
            <p style={{ color: '#dc2626', marginBottom: 0 }}>{ngrokStatus.error}</p>
          )}
          <p style={{ marginBottom: 0, marginTop: 4, fontSize: 12, color: '#6b7280' }}>
            {t('setting.system.ngrok.messages.callback_hint')}
          </p>
        </Message>
        <Form.Group inline>
          <Form.Checkbox
            checked={ngrokConfig.enabled}
            label={t('setting.system.ngrok.enabled')}
            onChange={(e, { checked }) => setNgrokConfig((prev) => ({ ...prev, enabled: checked }))}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.ngrok.authtoken')}
            placeholder={t('setting.system.ngrok.authtoken_placeholder')}
            type='password'
            autoComplete='new-password'
            value={ngrokConfig.authtoken}
            onChange={(e, { value }) => setNgrokConfig((prev) => ({ ...prev, authtoken: value }))}
          />
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.ngrok.domain')}
            placeholder={t('setting.system.ngrok.domain_placeholder')}
            value={ngrokConfig.domain}
            onChange={(e, { value }) => setNgrokConfig((prev) => ({ ...prev, domain: value }))}
          />
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.ngrok.region')}
            placeholder={t('setting.system.ngrok.region_placeholder')}
            value={ngrokConfig.region}
            onChange={(e, { value }) => setNgrokConfig((prev) => ({ ...prev, region: value }))}
          />
        </Form.Group>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <Button style={globalStyles.primaryButton} onClick={saveNgrokConfig} loading={ngrokLoading}>
            {t('setting.system.ngrok.buttons.save')}
          </Button>
          <Button style={globalStyles.button} onClick={startNgrok} disabled={ngrokStatus.running}>
            {t('setting.system.ngrok.buttons.start')}
          </Button>
          <Button style={globalStyles.button} onClick={stopNgrok} disabled={!ngrokStatus.running}>
            {t('setting.system.ngrok.buttons.stop')}
          </Button>
          <Button style={globalStyles.button} onClick={getNgrokStatus}>
            {t('setting.system.ngrok.buttons.refresh')}
          </Button>
        </div>
        </Form>

        <Divider />
        <Form loading={tunnelLoading} className='settings-form-stack'>
        <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: colors.gray, marginTop: 24, marginBottom: 16 }}>
          {t('setting.system.tunnel.title')}
          <Header.Subheader style={{ marginTop: 6 }}>
            {t('setting.system.tunnel.subtitle')}
          </Header.Subheader>
        </Header>
        <Message style={{ borderRadius: radius.md, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)', marginBottom: 16 }}>
          <Message.Header>{t('setting.system.tunnel.public_url')}</Message.Header>
          {tunnelStatus.running ? (
            <p style={{ marginBottom: 6 }}>
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#f0fdf4', color: '#16a34a', fontSize: 12, marginRight: 8 }}>
                {t('setting.system.tunnel.status_running')}
              </span>
              {tunnelStatus.public_url ? (
                <a href={tunnelStatus.public_url} target='_blank' rel='noopener noreferrer'>{tunnelStatus.public_url}</a>
              ) : t('setting.system.tunnel.no_public_url')}
              {tunnelStatus.public_url && (
                <Button size='mini' style={{ ...globalStyles.button, marginLeft: 8 }} onClick={copyTunnelUrl}>
                  {t('setting.system.tunnel.buttons.copy')}
                </Button>
              )}
            </p>
          ) : (
            <p style={{ marginBottom: 6 }}>
              <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: '#f5f5f5', color: '#6b7280', fontSize: 12, marginRight: 8 }}>
                {t('setting.system.tunnel.status_stopped')}
              </span>
            </p>
          )}
          {tunnelStatus.running && (
            <p style={{ marginBottom: 4, fontSize: 12, color: '#6b7280' }}>
              <strong>{t('setting.system.tunnel.latency')}:</strong>{' '}
              {tunnelStatus.latency_ms > 0
                ? t('setting.system.tunnel.ms', { n: tunnelStatus.latency_ms })
                : t('setting.system.tunnel.never')}
              {'  |  '}
              <strong>{t('setting.system.tunnel.last_ping')}:</strong>{' '}
              {tunnelStatus.last_ping_at > 0
                ? t('setting.system.tunnel.seconds_ago', { n: Math.max(0, Math.floor(Date.now() / 1000) - tunnelStatus.last_ping_at) })
                : t('setting.system.tunnel.never')}
              {'  |  '}
              <strong>{t('setting.system.tunnel.last_request')}:</strong>{' '}
              {tunnelStatus.last_request_ms > 0
                ? t('setting.system.tunnel.ms', { n: tunnelStatus.last_request_ms })
                : t('setting.system.tunnel.never')}
            </p>
          )}
          {tunnelStatus.error && (
            <p style={{ color: '#dc2626', marginBottom: 0 }}>{tunnelStatus.error}</p>
          )}
          <p style={{ marginBottom: 0, marginTop: 4, fontSize: 12, color: '#6b7280' }}>
            {t('setting.system.tunnel.messages.callback_hint')}
          </p>
        </Message>
        <Form.Group inline>
          <Form.Checkbox
            checked={tunnelConfig.enabled}
            label={t('setting.system.tunnel.enabled')}
            onChange={(e, { checked }) => setTunnelConfig((prev) => ({ ...prev, enabled: checked }))}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.tunnel.server_url')}
            placeholder={t('setting.system.tunnel.server_url_placeholder')}
            value={tunnelConfig.server_url}
            onChange={(e, { value }) => setTunnelConfig((prev) => ({ ...prev, server_url: value }))}
          />
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.tunnel.token')}
            placeholder={t('setting.system.tunnel.token_placeholder')}
            type='password'
            autoComplete='new-password'
            value={tunnelConfig.token}
            onChange={(e, { value }) => setTunnelConfig((prev) => ({ ...prev, token: value }))}
          />
        </Form.Group>
        <Form.Group widths='equal'>
          <Form.Input style={{ borderRadius: radius.md }}
            label={t('setting.system.tunnel.public_url')}
            placeholder={t('setting.system.tunnel.public_url_placeholder')}
            value={tunnelConfig.public_url}
            onChange={(e, { value }) => setTunnelConfig((prev) => ({ ...prev, public_url: value }))}
          />
        </Form.Group>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          <Button style={globalStyles.primaryButton} onClick={saveTunnelConfig} loading={tunnelLoading}>
            {t('setting.system.tunnel.buttons.save')}
          </Button>
          <Button style={globalStyles.button} onClick={startTunnel} disabled={tunnelStatus.running}>
            {t('setting.system.tunnel.buttons.start')}
          </Button>
          <Button style={globalStyles.button} onClick={stopTunnel} disabled={!tunnelStatus.running}>
            {t('setting.system.tunnel.buttons.stop')}
          </Button>
          <Button style={globalStyles.button} onClick={testTunnel} loading={tunnelTesting}>
            {t('setting.system.tunnel.buttons.test')}
          </Button>
          <Button style={globalStyles.button} onClick={getTunnelStatus}>
            {t('setting.system.tunnel.buttons.refresh')}
            </Button>
            </div>
            </Form>

            <Divider />

            <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: colors.gray, marginBottom: 12 }}>
            {t('setting.system.frp.title')}
            <Header.Subheader>
              {t('setting.system.frp.subtitle')}
            </Header.Subheader>
            </Header>

            <div style={{ marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{
              padding: '4px 12px',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              background: frpStateStyle.background,
              color: frpStateStyle.color,
            }}>
              {t(`setting.system.frp.status_${frpState}`)}
            </span>
            {frpStatus.public_url && (
              <>
                <a href={frpStatus.public_url} target='_blank' rel='noreferrer' style={{ color: colors.blue, fontSize: 13 }}>
                  {frpStatus.public_url}
                </a>
                <Button size='mini' compact onClick={copyFrpUrl}>{t('setting.system.frp.buttons.copy')}</Button>
              </>
            )}
            {!frpStatus.public_url && (
              <span style={{ color: colors.gray, fontSize: 13 }}>{t('setting.system.frp.no_public_url')}</span>
            )}
            {frpStatus.pid > 0 && (
              <span style={{ color: colors.gray, fontSize: 12 }}>{t('setting.system.frp.pid')}: {frpStatus.pid}</span>
            )}
            </div>

            {frpStatus.error && (
            <p style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{frpStatus.error}</p>
            )}

            <Form loading={frpLoading}>
            <Form.Checkbox
              checked={frpConfig.enabled}
              label={t('setting.system.frp.enabled')}
              onChange={(e, { checked }) => setFrpConfig((prev) => ({ ...prev, enabled: checked }))}
              style={{ marginBottom: 12 }}
            />
            <Form.Group widths='equal'>
              <Form.Input
                label={t('setting.system.frp.server_addr')}
                placeholder={t('setting.system.frp.server_addr_placeholder')}
                value={frpConfig.server_addr}
                onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, server_addr: value }))}
              />
              <Form.Input
                type='number'
                label={t('setting.system.frp.server_port')}
                placeholder={t('setting.system.frp.server_port_placeholder')}
                value={frpConfig.server_port}
                onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, server_port: parseInt(value) || 16666 }))}
              />
            </Form.Group>
            <Form.Input
              label={t('setting.system.frp.auth_token')}
              placeholder={t('setting.system.frp.auth_token_placeholder')}
              value={frpConfig.auth_token}
              onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, auth_token: value }))}
              style={{ marginBottom: 12 }}
            />
            <Form.Group widths='equal'>
              <Form.Input
                type='number'
                label={t('setting.system.frp.local_port')}
                placeholder={t('setting.system.frp.local_port_placeholder')}
                value={frpConfig.local_port}
                onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, local_port: parseInt(value) || 3000 }))}
              />
              {frpConfig.proxy_type === 'tcp' && (
                <Form.Input
                  type='number'
                  label={t('setting.system.frp.remote_port')}
                  placeholder={t('setting.system.frp.remote_port_placeholder')}
                  value={frpConfig.remote_port}
                  onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, remote_port: parseInt(value) || 3000 }))}
                />
              )}
              {frpConfig.proxy_type === 'http' && (
                <Form.Input
                  label={t('setting.system.frp.custom_domain')}
                  placeholder={t('setting.system.frp.custom_domain_placeholder')}
                  value={frpConfig.custom_domain}
                  onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, custom_domain: value }))}
                />
              )}
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('setting.system.frp.proxy_name')}
                placeholder={t('setting.system.frp.proxy_name_placeholder')}
                value={frpConfig.proxy_name}
                onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, proxy_name: value }))}
              />
              <Form.Dropdown
                label={t('setting.system.frp.proxy_type')}
                selection
                value={frpConfig.proxy_type}
                options={[
                  { key: 'tcp', text: 'tcp', value: 'tcp' },
                  { key: 'http', text: 'http', value: 'http' },
                ]}
                onChange={(e, { value }) => setFrpConfig((prev) => ({ ...prev, proxy_type: value }))}
              />
            </Form.Group>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Button style={globalStyles.primaryButton} onClick={saveFrpConfig} loading={frpLoading}>
                {t('setting.system.frp.buttons.save')}
              </Button>
              <Button style={globalStyles.button} onClick={startFrp} disabled={frpState === 'running' || frpState === 'starting'}>
                {t('setting.system.frp.buttons.start')}
              </Button>
              <Button style={globalStyles.button} onClick={stopFrp} disabled={frpState === 'stopped' || frpState === 'failed'}>
                {t('setting.system.frp.buttons.stop')}
              </Button>
              <Button style={globalStyles.button} onClick={getFrpStatus}>
                {t('setting.system.frp.buttons.refresh')}
              </Button>
            </div>
            </Form>

            <Divider />
        <section className='settings-section settings-encryption'>
        <Header as='h3' style={{ fontSize: 16, fontWeight: 700, color: colors.gray, marginBottom: 12 }}>
          通讯加密
          <Header.Subheader>
            开启后，对应渠道会自动识别请求中的敏感信息并加密转发，响应返回时自动解密。
          </Header.Subheader>
        </Header>
        {encryptionUsage > 0 && (
          <Message warning style={{ borderRadius: radius.md, marginBottom: 16 }}>
            <Message.Header>注意</Message.Header>
            <p>当前有 {encryptionUsage} 个渠道已启用加密通讯。重新生成密钥后，这些渠道会使用新密钥进行加解密。</p>
          </Message>
        )}
        <Message style={{ borderRadius: radius.md, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.16)', marginBottom: 16 }}>
          <Message.Header>说明</Message.Header>
          <p style={{ marginBottom: 0 }}>通讯加密会在开启该功能的渠道上自动工作：请求侧只负责写入占位符，响应侧只要看到 <code>[ENC:...]</code> 就解密。</p>
        </Message>
        <Tab
          className='studio-tabs settings-encryption-tabs'
          menu={{ secondary: true, pointing: true }}
          panes={[
            {
              menuItem: '密钥管理',
              render: () => (
                <Tab.Pane attached={false} style={{ border: 'none', boxShadow: 'none', padding: '8px 0 0' }}>
                  <Form loading={encryptionLoading}>
                    <Form.Group widths='equal'>
                      <Form.TextArea
                        label='AES-256 加密密钥'
                        placeholder='点击生成按钮生成密钥'
                        value={encryption.key}
                        readOnly
                        rows={3}
                        style={{ fontFamily: 'monospace', fontSize: 12, borderRadius: radius.md }}
                      />
                    </Form.Group>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <Button style={globalStyles.primaryButton} onClick={handleGenerateKey}>
                        随机生成密钥
                      </Button>
                    </div>
                    <Form.Group widths='equal'>
                      <Form.Input
                        label='加密盐（可选）'
                        placeholder='留空则不使用额外盐值'
                        value={encryption.salt}
                        onChange={(e, { value }) => setEncryption(prev => ({ ...prev, salt: value }))}
                        style={{ borderRadius: radius.md }}
                      />
                    </Form.Group>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button style={globalStyles.primaryButton} onClick={saveEncryptionSalt}>
                        保存加密盐
                      </Button>
                    </div>
                  </Form>
                </Tab.Pane>
              ),
            },
            {
              menuItem: '规则配置',
              render: () => (
                <Tab.Pane attached={false} style={{ border: 'none', boxShadow: 'none', padding: '8px 0 0' }}>
                  <Form loading={encryptionLoading}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                      <Button type='button' style={globalStyles.button} onClick={addEncryptionRule}>
                        新增规则
                      </Button>
                      <Button type='button' style={globalStyles.primaryButton} onClick={saveEncryptionRules}>
                        保存规则
                      </Button>
                      <Button type='button' style={globalStyles.button} onClick={resetEncryptionRules}>
                        恢复默认规则
                      </Button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {encryptionRules.map((rule, index) => (
                        <div
                          key={index}
                          style={{
                            border: '1px solid rgba(0,0,0,0.10)',
                            borderRadius: radius.md,
                            padding: '10px 12px 8px',
                            background: '#ffffff',
                          }}
                        >
                          <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 2.1fr auto auto', gap: 10, alignItems: 'end' }}>
                            <Form.Input
                              label='名称'
                              placeholder='例如：密码'
                              value={rule.name || ''}
                              onChange={(e, { value }) => updateEncryptionRule(index, { name: value })}
                              style={{ borderRadius: radius.md }}
                            />
                            <Form.Input
                              label='正则'
                              placeholder='有捕获组时加密第一个捕获组'
                              value={rule.pattern || ''}
                              onChange={(e, { value }) => updateEncryptionRule(index, { pattern: value })}
                              style={{ borderRadius: radius.md, fontFamily: 'monospace' }}
                            />
                            <Form.Checkbox
                              label='启用'
                              checked={!!rule.enabled}
                              onChange={(e, { checked }) => updateEncryptionRule(index, { enabled: checked })}
                              style={{ marginBottom: 8, minWidth: 48 }}
                            />
                            <Button
                              type='button'
                              style={{ ...globalStyles.button, background: '#fef2f2', color: '#dc2626', marginBottom: 6 }}
                              onClick={() => removeEncryptionRule(index)}
                            >
                              删除
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Form>
                </Tab.Pane>
              ),
            },
            {
              menuItem: '规则测试',
              render: () => (
                <Tab.Pane attached={false} style={{ border: 'none', boxShadow: 'none', padding: '8px 0 0' }}>
                  <Form loading={encryptionLoading}>
                    <Form.TextArea
                      label='测试上下文'
                      placeholder='输入一段准备发送给下游的上下文内容'
                      value={encryptionTestText}
                      onChange={(e, { value }) => setEncryptionTestText(value)}
                      rows={4}
                      style={{ fontFamily: 'monospace', fontSize: 12, borderRadius: radius.md }}
                    />
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                      <Button type='button' style={globalStyles.primaryButton} onClick={testEncryptionRules}>
                        测试规则策略
                      </Button>
                    </div>
                    <Form.TextArea
                      label='理论发送内容'
                      value={encryptionTestOutput}
                      readOnly
                      rows={5}
                      style={{ fontFamily: 'monospace', fontSize: 12, borderRadius: radius.md }}
                    />
                  </Form>
                </Tab.Pane>
              ),
            },
          ]}
        />

        <Modal
          open={showEncryptionConfirm}
          onClose={() => setShowEncryptionConfirm(false)}
          size='tiny'
          className='studio-modal'
          style={{ maxWidth: '450px' }}
        >
          <Modal.Header>确认重新生成密钥</Modal.Header>
          <Modal.Content>
            <p>当前有 {encryptionUsage} 个渠道正在使用加密通讯。重新生成密钥后，这些渠道会使用新密钥加解密，历史已加密但未返回的占位符将无法解密。</p>
            <p>是否继续重新生成？</p>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setShowEncryptionConfirm(false)}>
              取消
            </Button>
            <Button color='yellow' onClick={doGenerateKey}>
              确认重新生成
            </Button>
          </Modal.Actions>
        </Modal>
        </section>
      </Grid.Column>
    </Grid></div>
  );
};

export default SystemSetting;
